import { HttpClient } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withPreloading,
  withViewTransitions,
} from '@angular/router';

import { provideLottieOptions } from 'ngx-lottie';

import { EXTERNAL_FORM_CONTROLS, FORM_API_SERVICE, type IFormApiService } from '@forthbridge/forms';

import { AuthStrategy } from '@generator-strategies/auth-strategy';
import { LanguageStrategy } from '@generator-strategies/language-strategy';
import { TenantStrategy } from '@generator-strategies/tenant-strategy';

import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { PrimeNG } from 'primeng/config';

import { AppAuthStrategy } from '@core/auth/services/auth-strategy.service';
import { initSuperTokens } from '@core/auth/utils/supertokens-init';
import { LocalTranslationLoader } from '@core/i18n/translation.loader';
import { FormApiService } from '@core/services/form-api.service';
import { AppLanguageStrategy } from '@core/services/language-strategy.service';
import { PrimeNGLocaleService } from '@core/services/primeng-locale.service';
import { AppTenantStrategy } from '@core/services/tenant-strategy.service';

import { dissolveAnimation } from '@shared/animations/dissolve-animation';
import { HeightInput } from '@shared/components/height-input/height-input';
import { WeightInput } from '@shared/components/weight-input/weight-input';
import { SingleSelectButtonGroup, SingleSelectButtonGroupStrategy, StatePickerField } from '@shared/form-fields';
import { COMPONENT_DEFINITIONS } from '@shared/registry/component-definitions';
import { ComponentRegistry } from '@shared/registry/component-registry.service';

import { SWAGGER_BASE_PATHS } from '../swagger-services';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';

bootstrapApplication(AppComponent, {
  providers: [
    ...SWAGGER_BASE_PATHS.map((path) => ({ provide: path, useValue: environment.ollaApiUrl })),
    { provide: AuthStrategy, useClass: AppAuthStrategy },
    { provide: LanguageStrategy, useClass: AppLanguageStrategy },
    { provide: TenantStrategy, useClass: AppTenantStrategy },
    provideZoneChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      rippleEffect: false,
      mode: 'ios',
      useSetInputAPI: true,
      navAnimation: dissolveAnimation,
    }),
    provideRouter(routes, withPreloading(PreloadAllModules), withViewTransitions()),
    {
      provide: PrimeNG,
      useFactory: (): PrimeNG => new PrimeNG(),
    },
    provideAnimations(),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),

    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: LocalTranslationLoader,
          deps: [HttpClient],
        },
      }),
    ),
    provideAppInitializer(() => {
      inject(ComponentRegistry).register(COMPONENT_DEFINITIONS);
    }),
    provideAppInitializer(() => {
      initSuperTokens();
    }),
    provideAppInitializer(() => {
      inject(PrimeNGLocaleService);
    }),
    {
      provide: FORM_API_SERVICE,
      useFactory: (formApiService: FormApiService) => formApiService as unknown as IFormApiService,
      deps: [FormApiService],
    },
    {
      provide: EXTERNAL_FORM_CONTROLS,
      useValue: [
        {
          controlType: 'single_select_button_group',
          component: SingleSelectButtonGroup,
          strategy: new SingleSelectButtonGroupStrategy(),
        },
        {
          controlType: 'weight_input',
          component: WeightInput,
        },
        {
          controlType: 'height_input',
          component: HeightInput,
        },
        {
          controlType: 'state_picker',
          component: StatePickerField,
        },
      ],
    },
  ],
});
